import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";

import api from "../../../api";
import { isRequestable, openPaperPdf } from "./paperAccess";

/** What will happen to each selected item for the chosen group. */
const GROUP_OUTCOME = {
  assigned: { label: "Already assigned to this group", color: "info", skip: true },
  approved: { label: "Already approved for this group", color: "success", skip: true },
  pending: { label: "Already requested — pending", color: "warning", skip: true },
  rejected: { label: "Rejected before — will request again", color: "default", skip: false },
  not_requested: { label: "Will be requested", color: "primary", skip: false },
};

/**
 * Request access to the selected papers for one of the tutor's groups. On every group change it
 * asks the server which items that group already has, so the tutor sees "already assigned — View"
 * instead of sending a request the admin has nothing to do with.
 */
export default function PaperAccessRequestDialog({
  open,
  onClose,
  title,
  endpoint,
  paperType,
  pdfUrl,
  groups,
  initialGroupId = "",
  selected, // { [id]: paper }
  onRemove,
  onAdd,
  onSubmitted,
}) {
  const [groupId, setGroupId] = useState("");
  const [groupStatus, setGroupStatus] = useState({});
  const [checking, setChecking] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [addLoading, setAddLoading] = useState(false);

  const selectedList = useMemo(() => Object.values(selected), [selected]);
  const selectedKey = selectedList.map((p) => p.id).sort((a, b) => a - b).join(",");

  useEffect(() => {
    if (!open) return;
    setGroupId(initialGroupId || (groups.length === 1 ? groups[0].id : ""));
    setNote("");
    setAddQuery("");
  }, [open, initialGroupId, groups]);

  // Which selected items the chosen group already has.
  useEffect(() => {
    if (!open || !groupId || !selectedKey) {
      setGroupStatus({});
      return;
    }
    let cancelled = false;
    setChecking(true);
    api
      .get("tutor/paper-access/group-check", {
        params: { paper_type: paperType, course_plan_id: groupId, paper_ids: selectedKey.split(",") },
      })
      .then((res) => !cancelled && setGroupStatus(res.data?.data?.group_status || {}))
      .catch(() => !cancelled && setGroupStatus({}))
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
  }, [open, groupId, selectedKey, paperType]);

  // "Add more" searches the whole catalogue, not just the page behind the dialog.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(() => {
      setAddLoading(true);
      api
        .get(endpoint, { params: { search: addQuery.trim() || undefined, per_page: 20 } })
        .then((res) => {
          if (cancelled) return;
          const rows = res.data?.data?.data || [];
          setAddResults(rows.filter(isRequestable));
        })
        .catch(() => !cancelled && setAddResults([]))
        .finally(() => !cancelled && setAddLoading(false));
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, addQuery, endpoint]);

  const outcomeOf = (paper) => GROUP_OUTCOME[groupStatus[paper.id]] || GROUP_OUTCOME.not_requested;
  const toRequest = groupId ? selectedList.filter((p) => !outcomeOf(p).skip) : selectedList;
  const skipped = selectedList.length - toRequest.length;
  const selectedGroup = groups.find((g) => g.id === groupId);

  const handleSubmit = async () => {
    if (!groupId || toRequest.length === 0) return;
    setSubmitting(true);
    try {
      const res = await api.post("tutor/paper-access", {
        paper_type: paperType,
        paper_ids: toRequest.map((p) => p.id),
        course_plan_id: groupId,
        note: note.trim() || null,
      });
      const requested = res.data?.data?.requested ?? toRequest.length;
      toast.success(
        `${requested} access request${requested !== 1 ? "s" : ""} sent to the admin.` +
          (skipped ? ` ${skipped} already available for this group.` : "")
      );
      onSubmitted();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to submit access request.");
    } finally {
      setSubmitting(false);
    }
  };

  const sectionLabel = (text, extra) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {text}
      </Typography>
      {extra}
    </Stack>
  );

  const addable = addResults.filter((p) => !selected[p.id]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Typography variant="h6" component="span" fontWeight={700}>
          Request access
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title} for one of your groups. Items the group already has open straight away.
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 12, right: 12 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          select
          fullWidth
          required
          label="Group"
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          helperText={
            groups.length === 0
              ? "You aren't assigned to any group yet, so you can't request access."
              : selectedGroup?.course?.name
                ? `Course: ${selectedGroup.course.name}`
                : " "
          }
          error={groups.length === 0}
          sx={{ mb: 2 }}
        >
          {groups.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.name}
              {g.course?.name ? ` — ${g.course.name}` : ""}
            </MenuItem>
          ))}
        </TextField>

        {sectionLabel(
          `Selected (${selectedList.length})`,
          checking && <CircularProgress size={14} />
        )}

        <Stack spacing={1} sx={{ mb: 2 }}>
          {selectedList.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              Nothing selected yet — add papers below.
            </Typography>
          )}
          {selectedList.map((paper) => {
            const outcome = outcomeOf(paper);
            const assigned = groupStatus[paper.id] === "assigned";
            return (
              <Box
                key={paper.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.25,
                  pl: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: outcome.skip ? "divider" : "primary.light",
                  bgcolor: outcome.skip ? "#f9fafb" : "background.paper",
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} noWrap title={paper.name}>
                    {paper.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      ID {paper.id}
                    </Typography>
                    {groupId && !checking && (
                      <Chip size="small" label={outcome.label} color={outcome.color} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    )}
                  </Stack>
                </Box>
                {assigned && (
                  <Button size="small" startIcon={<VisibilityIcon />} onClick={() => openPaperPdf(pdfUrl(paper.id))}>
                    View
                  </Button>
                )}
                <IconButton size="small" onClick={() => onRemove(paper.id)} aria-label="Remove">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Stack>

        {groupId && skipped > 0 && !checking && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {skipped} of the selected item{skipped !== 1 ? "s are" : " is"} already available to or requested for
            this group and won't be requested again.
          </Alert>
        )}

        {sectionLabel("Add more")}
        <TextField
          fullWidth
          size="small"
          placeholder="Search papers to add…"
          value={addQuery}
          onChange={(e) => setAddQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: addLoading ? <CircularProgress size={16} /> : null,
          }}
          sx={{ mb: 1 }}
        />
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, maxHeight: 180, overflowY: "auto", mb: 2 }}>
          {addable.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
              {addLoading ? "Searching…" : "No more papers to add."}
            </Typography>
          ) : (
            addable.map((paper) => (
              <Box
                key={paper.id}
                onClick={() => onAdd(paper)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  cursor: "pointer",
                  "&:not(:last-of-type)": { borderBottom: "1px solid", borderColor: "divider" },
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={paper.name}>
                  {paper.name}
                </Typography>
                <AddIcon fontSize="small" color="primary" />
              </Box>
            ))
          )}
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Note (optional)"
          placeholder="Add a note for the administrator…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={!groupId || toRequest.length === 0 || submitting || checking}
          onClick={handleSubmit}
        >
          Request {toRequest.length > 0 ? toRequest.length : ""} {toRequest.length === 1 ? "item" : "items"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
